<template>
  <div class="vform17-vodalys-bridge">



    <div class="vform17-connection-url" v-if="vodalysConnectUrl">
      <f-textfield
        readonly
        :input="vodalysConnectUrl"
      >
        <template #label>{{ $vt('Connection URL') }}</template>
        <template #action>
          <div class="connection-url-actions">
            <f-button
              type="tertiary"
              @click.stop.prevent.capture="copyToClipboard(vodalysConnectUrl)"
            >
              {{ $vt('Copy') }}
              <f-icon icon="fa6-regular fa6-copy"></f-icon>
            </f-button>
            <f-button
              type="tertiary"
              :href="vodalysConnectUrl"
              blank
            >
              {{ $vt('Open') }}
              <f-icon icon="fa6-solid fa6-arrow-up-right-from-square"></f-icon>
            </f-button>
          </div>
        </template>
      </f-textfield>
    </div>

    <f-textfield
      class="vform17-connection-code"
      :input="formattedConnectionCode"
      :placeholder="$vt('Connection Code...')"
      readonly
    >
      <template #label>{{ $vt('Connection Code') }}</template>
      <template #action>
        <f-button 
          type="tertiary" 
          @click.stop.prevent="copyToClipboard(component.connection_code)"
          v-if="component.connection_code"
        >
          {{ $vt('Copy') }}
          <f-icon icon="fa6-regular fa6-copy"></f-icon>
        </f-button>
      </template>
    </f-textfield>

    <div class="vform17-download-url" v-if="component.download_url">
      <div class="download-link-row">
        <f-link
          v-if="resolvedDownloadUrl || !component.download_url.endsWith('.json')"
          type="tertiary"
          :href="resolvedDownloadUrl || component.download_url"
          blank
        >
          {{ $vt('Download Vodalys Bridge for Windows') }}
        </f-link>
        <span
          v-else
          class="download-link-disabled"
        >
          {{ $vt('Download Vodalys Bridge for Windows') }}
        </span>
        <span v-if="cachedDownloadVersion" class="download-version">
          v{{ cachedDownloadVersion }}
        </span>
      </div>
      <div v-if="manifestError" class="error-message">{{ manifestError }}</div>
    </div>

  </div>
</template>
<script>
export default {
  translate25Context: 'vodalys_bridge',
  props: ["component", "handler"],
  data() {
    return {
      isLoadingManifest: false,
      manifestError: null,
      cachedDownloadUrl: null,
      cachedDownloadVersion: null
    };
  },
  computed: {
    vodalysConnectUrl() {
      if (!this.component.connection_code) {
        return null;
      }
      let url = `vodalys://bridge?connect=${this.component.connection_code}`;
      if (window.location.hostname !== 'app.vodalys.io') {
        url += `&host=${encodeURIComponent(window.location.host)}`;
      }
      return url;
    },
    formattedConnectionCode() {
      if (!this.component.connection_code) {
        return null;
      }
      // Format: voda-XXXX-XXXX-XXXX-XXXX
      const code = this.component.connection_code;
      if (code.startsWith('voda-')) {
        // Extract the part after "voda-"
        const rest = code.substring(5);
        // Format the rest in groups of 4 characters
        const formatted = rest.match(/.{1,4}/g)?.join('-') || rest;
        return `voda-${formatted}`;
      }
      // If it doesn't start with "voda-", format all characters
      return code.match(/.{1,4}/g)?.join('-') || code;
    },
    resolvedDownloadUrl() {
      if (!this.component.download_url) {
        return null;
      }
      
      // If URL doesn't end with .json, use it directly
      if (!this.component.download_url.endsWith('.json')) {
        return this.component.download_url;
      }
      
      // If we have a cached URL, use it
      if (this.cachedDownloadUrl) {
        return this.cachedDownloadUrl;
      }
      
      // If manifest is being loaded or not yet fetched, return null
      // The watcher and mounted hook will trigger the fetch
      return null;
    }
  },
  methods: {
    emitUrls() {
      this.$emit('urls-change', {
        connectionUrl: this.vodalysConnectUrl,
        downloadUrl: this.resolvedDownloadUrl
      });
    },
    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          // Optionally show a success message
        }).catch(err => {
          console.error('Failed to copy text: ', err);
        });
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Fallback copy failed: ', err);
        }
        document.body.removeChild(textArea);
      }
    },
    async fetchTauriManifest(url) {
      if (!url || !url.endsWith('.json')) {
        this.manifestError = 'Invalid manifest URL';
        return;
      }
      
      this.isLoadingManifest = true;
      this.manifestError = null;
      
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const manifest = await response.json();
        
        // Validate manifest structure
        if (!manifest.platforms || !manifest.platforms['windows-x86_64']) {
          throw new Error('Invalid Tauri manifest: missing windows-x86_64 platform');
        }
        
        const windowsUrl = manifest.platforms['windows-x86_64'].url;
        if (!windowsUrl) {
          throw new Error('Invalid Tauri manifest: missing URL for windows-x86_64');
        }
        
        const manifestVersion = manifest.version
          || (manifest.package && manifest.package.version)
          || null;

        // Cache the resolved URL and version
        this.cachedDownloadUrl = windowsUrl;
        this.cachedDownloadVersion = manifestVersion;
      } catch (error) {
        console.error('Failed to fetch Tauri manifest:', error);
        this.manifestError = `Failed to load download information: ${error.message}`;
        // Don't set cachedDownloadUrl on error - button will remain disabled
        this.cachedDownloadVersion = null;
      } finally {
        this.isLoadingManifest = false;
      }
    }
  },
  watch: {
    'component.download_url'(newUrl) {
      // Reset cache when download_url changes
      this.cachedDownloadUrl = null;
      this.cachedDownloadVersion = null;
      this.manifestError = null;
      this.emitUrls();
      if (newUrl && newUrl.endsWith('.json')) {
        this.fetchTauriManifest(newUrl);
      }
    },
    vodalysConnectUrl() {
      this.emitUrls();
    },
    resolvedDownloadUrl() {
      this.emitUrls();
    }
  },
  mounted() {
    this.emitUrls();
    // Fetch manifest on mount if download_url is a JSON file
    if (this.component.download_url && this.component.download_url.endsWith('.json')) {
      this.fetchTauriManifest(this.component.download_url);
    }
  }
};
</script>

<style lang="scss">
.vform17-vodalys-bridge {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .vform17-download-url,
  .vform17-connection-url {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .download-link-row {
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
    }

    .download-link-disabled {
      color: #8c8c8c;
      opacity: 0.8;
    }

    .download-version {
      color: #8c8c8c;
      font-size: 12px;
    }

    .connection-url-actions {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: nowrap;
    }

    .f-label {
      margin: 0 0 6px 4px;
      font-family: Lato;
      font-weight: 600;
      font-size: 12px;
      line-height: 14px;
      color: #161615;
      display: flex;
      gap: 7px;
      align-items: baseline;
    }
  }
}
</style>

